import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { X, Check, MapPin, Save, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from './Icons';
import { updateGateCoordinates } from '../services/api';
import './GateMapPicker.css';

const parseFirebaseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp._seconds !== undefined) {
    return new Date(timestamp._seconds * 1000);
  }
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return null;
};

const FitBounds = ({ gates }) => {
  const map = useMap();
  
  React.useEffect(() => {
    if (gates.length > 0) {
      const bounds = [];
      gates.forEach(gate => {
        if (gate.coords) {
          gate.coords.forEach(coord => bounds.push(coord));
        }
      });
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [gates, map]);
  
  return null;
};

// ~7.5 feet per nudge click
const NUDGE_FEET = 7.5;
const FEET_TO_METERS = 0.3048;
const METERS_PER_DEGREE_LAT = 111320;

function parseGateCoords(gate) {
  let coords = gate.coordinates;
  if (typeof coords === 'string') {
    coords = JSON.parse(coords);
  }
  return coords;
}

const GateMapPicker = ({ gates, selectedGates, gateAssignments, currentDeviceId, onSelectGate, onClose, onGateCoordinatesUpdated }) => {
  const [hoveredGate, setHoveredGate] = useState(null);
  const [pendingSelections, setPendingSelections] = useState(new Set());

  // Mode: 'assign' (default gate assignment) or 'edit' (edit GPS endpoints)
  const [mode, setMode] = useState('assign');
  const [editingGateId, setEditingGateId] = useState(null);
  const [editedCoords, setEditedCoords] = useState(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editMessage, setEditMessage] = useState(null);

  // Process gates with coordinates
  const gateLines = useMemo(() => {
    return (gates || []).map(gate => {
      try {
        const coords = parseGateCoords(gate);

        if (Array.isArray(coords) && coords.length === 2) {
          const isAssignedToCurrentDevice = selectedGates.includes(gate.gateId);
          const assignedDevices = gateAssignments[gate.gateId] || [];
          const isAssignedToAnyDevice = assignedDevices.length > 0;
          const otherDevices = assignedDevices.filter(d => d !== currentDeviceId);

          // Use edited coords when this gate is being edited
          const useEdited = editingGateId === gate.gateId && editedCoords;
          const finalCoords = useEdited ? editedCoords : coords;
          
          return {
            id: gate.gateId,
            userId: gate.userId,
            userPrefix: gate.userId ? gate.userId.substring(0, 5) : '—',
            createdAtDate: parseFirebaseTimestamp(gate.createdAt),
            isAssignedToCurrentDevice,
            isAssignedToAnyDevice,
            assignedDevices,
            otherDevices,
            coords: [
              [finalCoords[0].latitude, finalCoords[0].longitude],
              [finalCoords[1].latitude, finalCoords[1].longitude]
            ],
            center: [
              (finalCoords[0].latitude + finalCoords[1].latitude) / 2,
              (finalCoords[0].longitude + finalCoords[1].longitude) / 2
            ]
          };
        }
      } catch (e) {
        console.error('Error parsing gate coordinates:', gate.gateId, e);
      }
      return null;
    }).filter(Boolean);
  }, [gates, selectedGates, gateAssignments, currentDeviceId, editingGateId, editedCoords]);

  const initialCenter = useMemo(() => {
    if (gateLines.length > 0) {
      const lat = gateLines.reduce((sum, g) => sum + g.center[0], 0) / gateLines.length;
      const lng = gateLines.reduce((sum, g) => sum + g.center[1], 0) / gateLines.length;
      return [lat, lng];
    }
    return [40.7128, -74.0060];
  }, [gateLines]);

  // Nudge the selected endpoint in a direction
  const nudgeEndpoint = useCallback((direction) => {
    if (selectedEndpoint === null || !editedCoords) return;
    
    const coord = editedCoords[selectedEndpoint];
    const nudgeMeters = NUDGE_FEET * FEET_TO_METERS;
    const deltaLat = nudgeMeters / METERS_PER_DEGREE_LAT;
    const deltaLon = nudgeMeters / (METERS_PER_DEGREE_LAT * Math.cos(coord.latitude * Math.PI / 180));
    
    const newCoords = editedCoords.map(c => ({ ...c }));
    
    switch (direction) {
      case 'north': newCoords[selectedEndpoint].latitude += deltaLat; break;
      case 'south': newCoords[selectedEndpoint].latitude -= deltaLat; break;
      case 'east': newCoords[selectedEndpoint].longitude += deltaLon; break;
      case 'west': newCoords[selectedEndpoint].longitude -= deltaLon; break;
      default: break;
    }
    
    setEditedCoords(newCoords);
  }, [selectedEndpoint, editedCoords]);

  // Keyboard arrow support when editing
  useEffect(() => {
    if (mode !== 'edit' || selectedEndpoint === null) return;
    
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowUp': e.preventDefault(); nudgeEndpoint('north'); break;
        case 'ArrowDown': e.preventDefault(); nudgeEndpoint('south'); break;
        case 'ArrowRight': e.preventDefault(); nudgeEndpoint('east'); break;
        case 'ArrowLeft': e.preventDefault(); nudgeEndpoint('west'); break;
        default: break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, selectedEndpoint, nudgeEndpoint]);

  const handleStartEditing = useCallback((gateId) => {
    const gate = (gates || []).find(g => g.gateId === gateId);
    if (!gate) return;
    
    try {
      const coords = parseGateCoords(gate);
      setEditingGateId(gateId);
      setEditedCoords(coords.map(c => ({ latitude: c.latitude, longitude: c.longitude })));
      setSelectedEndpoint(null);
      setEditMessage(null);
    } catch (e) {
      console.error('Error starting edit:', e);
    }
  }, [gates]);

  const handleCancelEdit = useCallback(() => {
    setEditingGateId(null);
    setEditedCoords(null);
    setSelectedEndpoint(null);
    setEditMessage(null);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingGateId || !editedCoords) return;
    setSavingEdit(true);
    setEditMessage(null);
    try {
      await updateGateCoordinates(editingGateId, editedCoords);
      setEditMessage({ type: 'success', text: 'Gate updated' });
      if (onGateCoordinatesUpdated) onGateCoordinatesUpdated();
      setTimeout(() => {
        setEditingGateId(null);
        setEditedCoords(null);
        setSelectedEndpoint(null);
        setEditMessage(null);
      }, 1200);
    } catch (err) {
      console.error('Error saving gate coordinates:', err);
      setEditMessage({ type: 'error', text: 'Save failed' });
    } finally {
      setSavingEdit(false);
    }
  }, [editingGateId, editedCoords, onGateCoordinatesUpdated]);

  const handleGateClick = useCallback((gate) => {
    if (mode === 'assign') {
      if (gate.isAssignedToCurrentDevice) return;
      setPendingSelections(prev => {
        const newSet = new Set(prev);
        if (newSet.has(gate.id)) {
          newSet.delete(gate.id);
        } else {
          newSet.add(gate.id);
        }
        return newSet;
      });
    } else {
      if (editingGateId !== gate.id) {
        handleStartEditing(gate.id);
      }
    }
  }, [mode, editingGateId, handleStartEditing]);

  const handleEndpointClick = useCallback((gate, endpointIdx, e) => {
    if (e) e.stopPropagation?.();
    
    if (mode === 'assign') {
      if (gate.isAssignedToCurrentDevice) return;
      setPendingSelections(prev => {
        const newSet = new Set(prev);
        if (newSet.has(gate.id)) {
          newSet.delete(gate.id);
        } else {
          newSet.add(gate.id);
        }
        return newSet;
      });
    } else {
      if (editingGateId === gate.id) {
        setSelectedEndpoint(endpointIdx);
      } else {
        handleStartEditing(gate.id);
      }
    }
  }, [mode, editingGateId, handleStartEditing]);

  const handleModeSwitch = useCallback((newMode) => {
    if (newMode === mode) return;
    if (newMode === 'assign') {
      handleCancelEdit();
    }
    setPendingSelections(new Set());
    setMode(newMode);
  }, [mode, handleCancelEdit]);

  const handleDone = () => {
    pendingSelections.forEach(gateId => {
      onSelectGate(gateId);
    });
    onClose();
  };

  const handleCancel = () => {
    setPendingSelections(new Set());
    handleCancelEdit();
    onClose();
  };

  const formatDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '—';
    return date.toLocaleDateString();
  };

  return (
    <div className="gate-map-picker-overlay">
      <div className="gate-map-picker">
        <div className="picker-header">
          <h3>
            <MapPin size={18} />
            Gate Map
          </h3>
          <div className="mode-toggle">
            <button
              className={`mode-btn ${mode === 'assign' ? 'active' : ''}`}
              onClick={() => handleModeSwitch('assign')}
            >
              Assign
            </button>
            <button
              className={`mode-btn ${mode === 'edit' ? 'active' : ''}`}
              onClick={() => handleModeSwitch('edit')}
            >
              Edit GPS
            </button>
          </div>
          <button className="close-btn" onClick={handleCancel}>
            <X size={20} />
          </button>
        </div>
        
        <div className="picker-content">
          <div className="map-container">
            <MapContainer
              center={initialCenter}
              zoom={15}
              maxZoom={22}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={22}
                maxNativeZoom={19}
              />
              
              <FitBounds gates={gateLines} />
              
              {gateLines.map((gate) => {
                const isSelected = pendingSelections.has(gate.id);
                const isHovered = hoveredGate === gate.id;
                const isEditing = mode === 'edit' && editingGateId === gate.id;
                
                let color = '#333';
                if (mode === 'assign') {
                  if (gate.isAssignedToCurrentDevice) {
                    color = '#9e9e9e';
                  } else if (isSelected) {
                    color = '#2196F3';
                  } else if (gate.isAssignedToAnyDevice) {
                    color = '#ff9800';
                  } else if (isHovered) {
                    color = '#fc5f21';
                  }
                } else {
                  if (isEditing) {
                    color = '#2196F3';
                  } else if (isHovered) {
                    color = '#fc5f21';
                  }
                }
                
                let weight = 3;
                if (mode === 'assign') {
                  if (gate.isAssignedToCurrentDevice) {
                    weight = 2;
                  } else if (isSelected) {
                    weight = 5;
                  } else if (isHovered) {
                    weight = 4;
                  }
                } else {
                  if (isEditing) {
                    weight = 5;
                  } else if (isHovered) {
                    weight = 4;
                  }
                }
                
                const dashArray = (mode === 'assign' && gate.isAssignedToCurrentDevice) ? '5, 5' : undefined;
                
                return (
                  <React.Fragment key={`${gate.id}-${isSelected}-${isHovered}-${isEditing}-${selectedEndpoint}`}>
                    <Polyline
                      positions={gate.coords}
                      pathOptions={{ color, weight, opacity: (mode === 'assign' && gate.isAssignedToCurrentDevice) ? 0.5 : 1, dashArray }}
                      eventHandlers={{
                        click: () => handleGateClick(gate),
                        mouseover: () => setHoveredGate(gate.id),
                        mouseout: () => setHoveredGate(null)
                      }}
                    >
                      <Tooltip sticky>
                        <div className="gate-tooltip">
                          <strong>{gate.id}</strong>
                          {mode === 'assign' && (
                            <>
                              {gate.isAssignedToCurrentDevice && <span className="assigned-badge current">This Device</span>}
                              {gate.otherDevices.length > 0 && (
                                <span className="assigned-badge other">
                                  {gate.otherDevices.length} other device{gate.otherDevices.length > 1 ? 's' : ''}
                                </span>
                              )}
                              {isSelected && <span className="selected-badge">Selected</span>}
                            </>
                          )}
                          {mode === 'edit' && isEditing && (
                            <span className="selected-badge">Editing</span>
                          )}
                          <div className="tooltip-row">User: {gate.userPrefix}</div>
                          <div className="tooltip-row">Created: {formatDate(gate.createdAtDate)}</div>
                          {mode === 'assign' && gate.otherDevices.length > 0 && (
                            <div className="tooltip-row devices">
                              Assigned to: {gate.otherDevices.join(', ')}
                            </div>
                          )}
                          <div className="tooltip-hint">
                            {mode === 'assign' ? (
                              gate.isAssignedToCurrentDevice ? 'Already assigned' :
                              isSelected ? 'Click to deselect' : 'Click to select'
                            ) : (
                              isEditing ? 'Click an endpoint to edit' : 'Click to edit this gate'
                            )}
                          </div>
                        </div>
                      </Tooltip>
                    </Polyline>
                    
                    {gate.coords.map((coord, idx) => {
                      const isEditEndpoint = isEditing && selectedEndpoint === idx;
                      let epColor = color;
                      let epRadius = isSelected ? 7 : 5;
                      let epWeight = isSelected ? 2 : 1;
                      
                      if (isEditing) {
                        epColor = isEditEndpoint ? '#e91e63' : '#2196F3';
                        epRadius = isEditEndpoint ? 9 : 7;
                        epWeight = isEditEndpoint ? 3 : 2;
                      }
                      
                      return (
                        <CircleMarker
                          key={`${gate.id}-ep-${idx}-${isSelected}-${isEditEndpoint}`}
                          center={coord}
                          radius={epRadius}
                          pathOptions={{
                            fillColor: epColor,
                            fillOpacity: (mode === 'assign' && gate.isAssignedToCurrentDevice) ? 0.5 : 0.9,
                            color: isEditEndpoint ? '#fff' : (isEditing ? '#fff' : '#fff'),
                            weight: epWeight
                          }}
                          eventHandlers={{
                            click: (e) => handleEndpointClick(gate, idx, e),
                            mouseover: () => setHoveredGate(gate.id),
                            mouseout: () => setHoveredGate(null)
                          }}
                        >
                          {isEditing && (
                            <Tooltip permanent={isEditEndpoint} direction="top">
                              Point {idx === 0 ? 'A' : 'B'}
                              {isEditEndpoint && ' (selected)'}
                            </Tooltip>
                          )}
                        </CircleMarker>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </MapContainer>

            {/* Floating edit panel */}
            {mode === 'edit' && editingGateId && (
              <div className="gate-edit-panel">
                <div className="edit-panel-header">
                  <strong>{editingGateId}</strong>
                  <button className="edit-panel-close" onClick={handleCancelEdit}>
                    <X size={14} />
                  </button>
                </div>

                <div className="endpoint-buttons">
                  <button
                    className={`endpoint-btn ${selectedEndpoint === 0 ? 'active' : ''}`}
                    onClick={() => setSelectedEndpoint(0)}
                  >
                    Point A
                  </button>
                  <button
                    className={`endpoint-btn ${selectedEndpoint === 1 ? 'active' : ''}`}
                    onClick={() => setSelectedEndpoint(1)}
                  >
                    Point B
                  </button>
                </div>

                {selectedEndpoint !== null && editedCoords && (
                  <div className="coord-display">
                    {editedCoords[selectedEndpoint].latitude.toFixed(7)}, {editedCoords[selectedEndpoint].longitude.toFixed(7)}
                  </div>
                )}

                <div className="dpad">
                  <div className="dpad-spacer" />
                  <button
                    className="dpad-btn"
                    onClick={() => nudgeEndpoint('north')}
                    disabled={selectedEndpoint === null}
                    title="Move north (~7.5 ft)"
                  >
                    <ChevronUp size={18} />
                  </button>
                  <div className="dpad-spacer" />
                  <button
                    className="dpad-btn"
                    onClick={() => nudgeEndpoint('west')}
                    disabled={selectedEndpoint === null}
                    title="Move west (~7.5 ft)"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div className="dpad-center">~7.5 ft</div>
                  <button
                    className="dpad-btn"
                    onClick={() => nudgeEndpoint('east')}
                    disabled={selectedEndpoint === null}
                    title="Move east (~7.5 ft)"
                  >
                    <ChevronRight size={18} />
                  </button>
                  <div className="dpad-spacer" />
                  <button
                    className="dpad-btn"
                    onClick={() => nudgeEndpoint('south')}
                    disabled={selectedEndpoint === null}
                    title="Move south (~7.5 ft)"
                  >
                    <ChevronDown size={18} />
                  </button>
                  <div className="dpad-spacer" />
                </div>

                {selectedEndpoint === null && (
                  <div className="edit-hint">Click an endpoint on the map to select it</div>
                )}
                {selectedEndpoint !== null && (
                  <div className="edit-hint">Use arrows or keyboard to nudge</div>
                )}

                {editMessage && (
                  <div className={`edit-message ${editMessage.type}`}>
                    {editMessage.text}
                  </div>
                )}

                <div className="edit-panel-actions">
                  <button className="edit-cancel-btn" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                  <button
                    className="edit-save-btn"
                    onClick={handleSaveEdit}
                    disabled={savingEdit}
                  >
                    <Save size={14} />
                    {savingEdit ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="picker-legend">
            {mode === 'assign' ? (
              <>
                <div className="legend-item">
                  <span className="legend-line available"></span>
                  Available
                </div>
                <div className="legend-item">
                  <span className="legend-line selected"></span>
                  Selected ({pendingSelections.size})
                </div>
                <div className="legend-item">
                  <span className="legend-line other-device"></span>
                  Assigned (other device)
                </div>
                <div className="legend-item">
                  <span className="legend-line current-device"></span>
                  Assigned (this device)
                </div>
              </>
            ) : (
              <>
                <div className="legend-item">
                  <span className="legend-line available"></span>
                  Gate
                </div>
                <div className="legend-item">
                  <span className="legend-line editing"></span>
                  Editing
                </div>
                <div className="legend-item">
                  <span className="legend-dot endpoint-active"></span>
                  Selected endpoint
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="picker-footer">
          {mode === 'assign' ? (
            <>
              <button className="cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
              <button 
                className="done-btn" 
                onClick={handleDone}
                disabled={pendingSelections.size === 0}
              >
                <Check size={16} />
                Add {pendingSelections.size} Gate{pendingSelections.size !== 1 ? 's' : ''}
              </button>
            </>
          ) : (
            <button className="cancel-btn" onClick={handleCancel}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GateMapPicker;
